/**
 * Every robot's presence for the active site, kept current without new
 * data (twin slice 1, plan W3 step 7): the one hook the marks, the
 * drawer, the attention list and the locate resolver read.
 *
 * It recomputes on every render -- cheap, robots times cars -- and the
 * tracker hands back the SAME map when nothing a mark shows changed, so
 * a consumer memoised on the result does no work for a parked fleet.
 * What makes it render without new data is the boundary tick: the
 * soonest moment any robot's reading would flip (checking → lost, live
 * → quiet → lost, settling → live).
 */
import { useRef } from 'react';
import type { LiftViewModel } from '../../api/adapters';
import type { RobotStateView } from '../../api/rmf-client';
import { recordedPosesOf, useRobotLastPoses } from '../../api/hooks/useRobotLastPoses';
import { useRobotReceipts } from '../../api/robot-receipts';
import { useBoundaryTick } from '../../state/clock';
import { useSafeSiteId } from '../../state/useActiveSite';
import { PresenceTracker, rideBookFor, type RobotPresence } from './robot-presence';

export function useRobotPresence(
  robots: readonly RobotStateView[] | undefined,
  lifts: readonly LiftViewModel[] | undefined,
): ReadonlyMap<string, RobotPresence> {
  const siteId = useSafeSiteId();
  const { book } = useRobotReceipts(siteId);
  const lastPoses = useRobotLastPoses();

  // One tracker per site: identities never cross sites. Ride memory is
  // not the tracker's; it is the site's, shared with every other mount
  // and fed by `useLiveData` (`RideBook`).
  const tracker = useRef<{ site: string; t: PresenceTracker } | null>(null);
  if (!tracker.current || tracker.current.site !== siteId) {
    tracker.current = { site: siteId, t: new PresenceTracker() };
  }
  const t = tracker.current.t;
  const presence = t.update({
    robots,
    lifts,
    receipts: book,
    recorded: recordedPosesOf(lastPoses.data),
    rides: rideBookFor(siteId),
    now: book.now(),
  });
  useBoundaryTick(t.nextBoundaryMs());
  return presence;
}
